import { pushMapArrayVal } from "@/lib/common-utils";
import { Currency } from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/util";
import { defaultTimingFilters } from "@/module/service-configs-module/statistics-visualization-module/second-timings/trading-page";
import {
  HeroResultsDto,
  PeriodStatisticsDto,
  PlayerTimingsDurationPointDto,
  PlayerTimingsPeriodDto,
  PlayersCategorizedResultsDto,
} from "@/shared/api";

export const createDefaultStat = (): PlayersCategorizedResultsDto => ({
  unknown_winners_count: 0,
  weak_winners_count: 0,
  middle_winners_count: 0,
  strong_winners_count: 0,

  unknown_winner_hands: 0,
  weak_winner_hands: 0,
  middle_winner_hands: 0,
  strong_winner_hands: 0,

  unknown_winner_gross_profit: 0,
  weak_winner_gross_profit: 0,
  middle_winner_gross_profit: 0,
  strong_winner_gross_profit: 0,

  unknown_winner_gross_profit_bb: 0,
  weak_winner_gross_profit_bb: 0,
  middle_winner_gross_profit_bb: 0,
  strong_winner_gross_profit_bb: 0,

  unknown_winner_gross_profit_usd: 0,
  weak_winner_gross_profit_usd: 0,
  middle_winner_gross_profit_usd: 0,
  strong_winner_gross_profit_usd: 0,

  unknown_winner_rake: 0,
  weak_winner_rake: 0,
  middle_winner_rake: 0,
  strong_winner_rake: 0,

  unknown_winner_rake_bb: 0,
  weak_winner_rake_bb: 0,
  middle_winner_rake_bb: 0,
  strong_winner_rake_bb: 0,

  unknown_winner_rake_usd: 0,
  weak_winner_rake_usd: 0,
  middle_winner_rake_usd: 0,
  strong_winner_rake_usd: 0,

  unknown_losers_count: 0,
  weak_losers_count: 0,
  middle_losers_count: 0,
  strong_losers_count: 0,

  unknown_loser_hands: 0,
  weak_loser_hands: 0,
  middle_loser_hands: 0,
  strong_loser_hands: 0,

  unknown_loser_gross_profit: 0,
  weak_loser_gross_profit: 0,
  middle_loser_gross_profit: 0,
  strong_loser_gross_profit: 0,

  unknown_loser_gross_profit_bb: 0,
  weak_loser_gross_profit_bb: 0,
  middle_loser_gross_profit_bb: 0,
  strong_loser_gross_profit_bb: 0,

  unknown_loser_gross_profit_usd: 0,
  weak_loser_gross_profit_usd: 0,
  middle_loser_gross_profit_usd: 0,
  strong_loser_gross_profit_usd: 0,

  unknown_loser_rake: 0,
  weak_loser_rake: 0,
  middle_loser_rake: 0,
  strong_loser_rake: 0,

  unknown_loser_rake_bb: 0,
  weak_loser_rake_bb: 0,
  middle_loser_rake_bb: 0,
  strong_loser_rake_bb: 0,

  unknown_loser_rake_usd: 0,
  weak_loser_rake_usd: 0,
  middle_loser_rake_usd: 0,
  strong_loser_rake_usd: 0,
});

export const createDefaultHero = (): HeroResultsDto => ({
  hero_count: 0,
  hero_hands_count: 0,
  hero_gross_profit: 0,
  hero_gross_profit_bb: 0,
  hero_gross_profit_usd: 0,
  hero_rake: 0,
  hero_rake_bb: 0,
  hero_rake_usd: 0,
});

const createDefaultDataSet = (): BarData => ({
  periods: [],
  data: {
    AU: new Map(),
    Rake: new Map(),
    Hands: new Map(),
    GrossProfit: new Map(),
    AvgBigBlind: new Map(),
    AvgChipPrice: new Map(),
  },
});

export interface SummarizedOpponent {
  winnersCount: number;
  winnerHands: number;
  winnerGrossProfit: number;
  winnerGrossProfitBB: number;
  winnerGrossProfitUsd: number;
  winnerRake: number;
  winnerRakeBB: number;
  winnerRakeUsd: number;

  losersCount: number;
  loserHands: number;
  loserGrossProfit: number;
  loserGrossProfitBB: number;
  loserGrossProfitUsd: number;
  loserRake: number;
  loserRakeBB: number;
  loserRakeUsd: number;
}

export const accumulateSubOpponentsData = (
  i: PeriodSummarizedResult,
  currency: Currency,
  winnerNetWinsMap: Map<string, number[]>,
  winnerRakesMap: Map<string, number[]>,
  losersRakesMap: Map<string, number[]>
) => {
  i.subOpponents.forEach((subOpponent, index) => {
    const winnerNetWinKey = `SubOpp ${index} Net Win`;
    const winnerRakeKey = `SubOpp ${index} Rake`;
    const losersRakeKey = `SubOpp ${index} Losers Rake`;

    const winnerNetWins = winnerNetWinsMap.get(winnerNetWinKey) ?? [];
    const winnerRakes = winnerRakesMap.get(winnerRakeKey) ?? [];
    const losersRakes = losersRakesMap.get(losersRakeKey) ?? [];

    switch (currency) {
      case "Chips":
        winnerNetWins.push(subOpponent.winnerGrossProfit - subOpponent.winnerRake);
        winnerRakes.push(subOpponent.winnerRake);
        losersRakes.push(subOpponent.loserRake);
        break;
      case "BB":
        winnerNetWins.push(subOpponent.winnerGrossProfitBB - subOpponent.winnerRakeBB);
        winnerRakes.push(subOpponent.winnerRakeBB);
        losersRakes.push(subOpponent.loserRakeBB);
        break;
      case "Usd":
        winnerNetWins.push(subOpponent.winnerGrossProfitUsd - subOpponent.winnerRakeUsd);
        winnerRakes.push(subOpponent.winnerRakeUsd);
        losersRakes.push(subOpponent.loserRakeUsd);
        break;
    }

    winnerNetWinsMap.set(winnerNetWinKey, winnerNetWins);
    winnerRakesMap.set(winnerRakeKey, winnerRakes);
    losersRakesMap.set(losersRakeKey, losersRakes);
  });
};

export const calcNormalizedOpponentData = (stat: PeriodSummarizedResult) => {
  let winnerGrossProfit = stat.winnerGrossProfit;
  let winnerGrossProfitBB = stat.winnerGrossProfitBB;
  let winnerGrossProfitUsd = stat.winnerGrossProfitUsd;
  let winnerRake = stat.winnerRake;
  let winnerRakeBB = stat.winnerRakeBB;
  let winnerRakeUsd = stat.winnerRakeUsd;

  let loserGrossProfit = stat.loserGrossProfit;
  let loserGrossProfitBB = stat.winnerGrossProfitBB;
  let loserGrossProfitUsd = stat.loserGrossProfitUsd;
  let loserRake = stat.loserRake;
  let loserRakeBB = stat.loserRakeBB;
  let loserRakeUsd = stat.loserRakeUsd;

  stat.subOpponents.forEach((i) => {
    winnerGrossProfit -= i.winnerGrossProfit;
    winnerGrossProfitBB -= i.winnerGrossProfitBB;
    winnerGrossProfitUsd -= i.winnerGrossProfitUsd;
    winnerRake -= i.winnerRake;
    winnerRakeBB -= i.winnerRakeBB;
    winnerRakeUsd -= i.winnerRakeUsd;

    loserGrossProfit -= i.loserGrossProfit;
    loserGrossProfitBB -= i.loserGrossProfitBB;
    loserGrossProfitUsd -= i.loserGrossProfitUsd;
    loserRake -= i.loserRake;
    loserRakeBB -= i.loserRakeBB;
    loserRakeUsd -= i.loserRakeUsd;
  });

  return {
    winnerGrossProfit,
    winnerGrossProfitBB,
    winnerGrossProfitUsd,
    winnerRake,
    winnerRakeBB,
    winnerRakeUsd,
    loserGrossProfit,
    loserGrossProfitBB,
    loserGrossProfitUsd,
    loserRake,
    loserRakeBB,
    loserRakeUsd,
  };
};

export interface PeriodSummarizedResult extends HeroResultsDto, SummarizedOpponent {
  period: string;
  subOpponents: SummarizedOpponent[];
}

export interface CountTotalResult extends SummarizedOpponent {
  categorized: PlayersCategorizedResultsDto;
}

export type MetricKey =
  | "AU"
  | "Rake"
  | "Hands"
  | "GrossProfit"
  | "AvgBigBlind"
  | "AvgChipPrice";

export type DataSet = {
  [key in MetricKey]: Map<string, number[]>;
};

export interface BarData {
  periods: string[];
  data: DataSet;
}

export const DATA_SET_KEYS = {
  HERO_KEY: "hero",
  WINNERS_KEY: "winners",
  LOSERS_KEY: "losers",
  SUB_OP_PREFIX: "sub_opponent",
};

export const currencyModifier = (currency: Currency, bb: number, chip_price: number) => {
  if (currency === "BB") {
    return 1 / (bb || 1);
  }

  if (currency === "Usd") {
    return chip_price || 1;
  }

  return 1;
};

export const computeNormalizedSummarizedDataSet = (data: BarData): BarData => {
  const result = { ...data };

  const computeMetric = (metrics: Map<string, number[]>) => {
    metrics.entries().forEach(([key, value]) => {
      if (!key.startsWith(DATA_SET_KEYS.SUB_OP_PREFIX)) return;

      if (key.endsWith(DATA_SET_KEYS.WINNERS_KEY)) {
        const winnersMetric = metrics.get(DATA_SET_KEYS.WINNERS_KEY) || [];

        value.forEach((num, idx) => {
          winnersMetric[idx] = (winnersMetric[idx] || 0) - num;
        });

        metrics.set(DATA_SET_KEYS.WINNERS_KEY, winnersMetric);
      }

      if (key.endsWith(DATA_SET_KEYS.LOSERS_KEY)) {
        const losersGrossProfit = metrics.get(DATA_SET_KEYS.LOSERS_KEY) || [];

        value.forEach((num, idx) => {
          losersGrossProfit[idx] = (losersGrossProfit[idx] || 0) - num;
        });

        metrics.set(DATA_SET_KEYS.LOSERS_KEY, losersGrossProfit);
      }
    });

    return metrics;
  };

  result.data.GrossProfit = computeMetric(result.data.GrossProfit);
  result.data.Hands = computeMetric(result.data.Hands);
  result.data.AU = computeMetric(result.data.AU);
  result.data.Rake = computeMetric(result.data.Rake);

  return result;
};

export const mapSummarizedDataSet = (data: PeriodSummarizedResult[]): BarData => {
  return data.reduce((prev, cur) => {
    prev.periods.push(cur.period);

    pushMapArrayVal(prev.data.AU, DATA_SET_KEYS.HERO_KEY, cur.hero_count);
    pushMapArrayVal(prev.data.AU, DATA_SET_KEYS.WINNERS_KEY, cur.winnersCount);
    pushMapArrayVal(prev.data.AU, DATA_SET_KEYS.LOSERS_KEY, cur.losersCount);

    pushMapArrayVal(prev.data.Rake, DATA_SET_KEYS.HERO_KEY, cur.hero_rake);
    pushMapArrayVal(prev.data.Rake, DATA_SET_KEYS.WINNERS_KEY, cur.winnerRake);
    pushMapArrayVal(prev.data.Rake, DATA_SET_KEYS.LOSERS_KEY, cur.loserRake);

    pushMapArrayVal(prev.data.Hands, DATA_SET_KEYS.HERO_KEY, cur.hero_hands_count);
    pushMapArrayVal(prev.data.Hands, DATA_SET_KEYS.WINNERS_KEY, cur.winnerHands);
    pushMapArrayVal(prev.data.Hands, DATA_SET_KEYS.LOSERS_KEY, cur.loserHands);

    pushMapArrayVal(prev.data.GrossProfit, DATA_SET_KEYS.HERO_KEY, cur.hero_gross_profit);
    pushMapArrayVal(prev.data.GrossProfit, DATA_SET_KEYS.WINNERS_KEY, cur.winnerGrossProfit);
    pushMapArrayVal(prev.data.GrossProfit, DATA_SET_KEYS.LOSERS_KEY, cur.loserGrossProfit);

    cur.subOpponents.forEach((subOpponent, index) => {
      pushMapArrayVal(
        prev.data.AU,
        `${DATA_SET_KEYS.SUB_OP_PREFIX}_${index}_${DATA_SET_KEYS.WINNERS_KEY}`,
        subOpponent.winnersCount
      );
      pushMapArrayVal(
        prev.data.AU,
        `${DATA_SET_KEYS.SUB_OP_PREFIX}_${index}_${DATA_SET_KEYS.LOSERS_KEY}`,
        subOpponent.losersCount
      );

      pushMapArrayVal(
        prev.data.Rake,
        `${DATA_SET_KEYS.SUB_OP_PREFIX}_${index}_${DATA_SET_KEYS.WINNERS_KEY}`,
        subOpponent.winnerRake
      );
      pushMapArrayVal(
        prev.data.Rake,
        `${DATA_SET_KEYS.SUB_OP_PREFIX}_${index}_${DATA_SET_KEYS.LOSERS_KEY}`,
        subOpponent.loserRake
      );

      pushMapArrayVal(
        prev.data.Hands,
        `${DATA_SET_KEYS.SUB_OP_PREFIX}_${index}_${DATA_SET_KEYS.WINNERS_KEY}`,
        subOpponent.winnerHands
      );
      pushMapArrayVal(
        prev.data.Hands,
        `${DATA_SET_KEYS.SUB_OP_PREFIX}_${index}_${DATA_SET_KEYS.LOSERS_KEY}`,
        subOpponent.loserHands
      );

      pushMapArrayVal(
        prev.data.GrossProfit,
        `${DATA_SET_KEYS.SUB_OP_PREFIX}_${index}_${DATA_SET_KEYS.WINNERS_KEY}`,
        subOpponent.winnerGrossProfit
      );
      pushMapArrayVal(
        prev.data.GrossProfit,
        `${DATA_SET_KEYS.SUB_OP_PREFIX}_${index}_${DATA_SET_KEYS.LOSERS_KEY}`,
        subOpponent.loserGrossProfit
      );
    });

    return prev;
  }, createDefaultDataSet() as BarData);
};

export const summarizeOpponent = (item: PlayersCategorizedResultsDto): SummarizedOpponent => {
  return {
    winnersCount:
      item.weak_winners_count +
      item.middle_winners_count +
      item.strong_winners_count +
      item.unknown_winners_count,
    winnerHands:
      item.weak_winner_hands +
      item.middle_winner_hands +
      item.strong_winner_hands +
      item.unknown_winner_hands,

    winnerGrossProfit:
      item.weak_winner_gross_profit +
      item.middle_winner_gross_profit +
      item.strong_winner_gross_profit +
      item.unknown_winner_gross_profit,
    winnerGrossProfitBB:
      item.weak_winner_gross_profit_bb +
      item.middle_winner_gross_profit_bb +
      item.strong_winner_gross_profit_bb +
      item.unknown_winner_gross_profit_bb,
    winnerGrossProfitUsd:
      item.weak_winner_gross_profit_usd +
      item.middle_winner_gross_profit_usd +
      item.strong_winner_gross_profit_usd +
      item.unknown_winner_gross_profit_usd,

    winnerRake:
      item.weak_winner_rake +
      item.middle_winner_rake +
      item.strong_winner_rake +
      item.unknown_winner_rake,
    winnerRakeBB:
      item.weak_winner_rake_bb +
      item.middle_winner_rake_bb +
      item.strong_winner_rake_bb +
      item.unknown_winner_rake_bb,
    winnerRakeUsd:
      item.weak_winner_rake_usd +
      item.middle_winner_rake_usd +
      item.strong_winner_rake_usd +
      item.unknown_winner_rake_usd,

    // Losers
    losersCount:
      item.weak_losers_count +
      item.middle_losers_count +
      item.strong_losers_count +
      item.unknown_losers_count,
    loserHands:
      item.weak_loser_hands +
      item.middle_loser_hands +
      item.strong_loser_hands +
      item.unknown_loser_hands,

    loserGrossProfit:
      item.weak_loser_gross_profit +
      item.middle_loser_gross_profit +
      item.strong_loser_gross_profit +
      item.unknown_loser_gross_profit,
    loserGrossProfitBB:
      item.weak_loser_gross_profit_bb +
      item.middle_loser_gross_profit_bb +
      item.strong_loser_gross_profit_bb +
      item.unknown_loser_gross_profit_bb,
    loserGrossProfitUsd:
      item.weak_loser_gross_profit_usd +
      item.middle_loser_gross_profit_usd +
      item.strong_loser_gross_profit_usd +
      item.unknown_loser_gross_profit_usd,

    loserRake:
      item.weak_loser_rake +
      item.middle_loser_rake +
      item.strong_loser_rake +
      item.unknown_loser_rake,
    loserRakeBB:
      item.weak_loser_rake_bb +
      item.middle_loser_rake_bb +
      item.strong_loser_rake_bb +
      item.unknown_loser_rake_bb,
    loserRakeUsd:
      item.weak_loser_rake_usd +
      item.middle_loser_rake_usd +
      item.strong_loser_rake_usd +
      item.unknown_loser_rake_usd,
  } as SummarizedOpponent;
};

export const summarizeStatistic = (arr: PeriodStatisticsDto[]): PeriodSummarizedResult[] => {
  return arr.map((item) => {
    const summarizedOpponent = summarizeOpponent(item.opponent_results);
    const summarizedSubOpponents = item.sub_opponent_results.map((i) => summarizeOpponent(i));

    return {
      period: item.period,
      hero_count: item.hero_results.hero_count,
      hero_hands_count: item.hero_results.hero_hands_count,
      hero_gross_profit: item.hero_results.hero_gross_profit,
      hero_gross_profit_bb: item.hero_results.hero_gross_profit_bb,
      hero_gross_profit_usd: item.hero_results.hero_gross_profit_usd,
      hero_rake: item.hero_results.hero_rake,
      hero_rake_bb: item.hero_results.hero_rake_bb,
      hero_rake_usd: item.hero_results.hero_rake_usd,
      ...summarizedOpponent,
      subOpponents: summarizedSubOpponents,
    };
  });
};

export const sumOpponentTotal = (val: CountTotalResult): HeroResultsDto => {
  const avgMod = Number(val.winnerHands > 0) + Number(val.loserHands > 0);

  return {
    hero_count: val.losersCount + val.winnersCount,
    hero_hands_count: val.loserHands + val.winnerHands,
    // hero_avg_chip_price:
    //   avgMod > 0 ? (val.loserAvgChipPrice + val.winnerAvgChipPrice) / avgMod : 0,
    // hero_avg_big_blind:
    //   avgMod > 0 ? (val.loserAvgBigBlind + val.winnerAvgBigBlind) / avgMod : 0,
    hero_gross_profit: val.loserGrossProfit + val.winnerGrossProfit,
    hero_rake: val.loserRake + val.winnerRake,
  } as HeroResultsDto;
};

export const countTotal = (arr: PlayersCategorizedResultsDto[]): CountTotalResult => {
  const result = arr.reduce((prev, cur) => {
    prev.unknown_winners_count += cur.unknown_winner_hands;
    prev.weak_winners_count += cur.weak_winner_hands;
    prev.middle_winners_count += cur.middle_winner_hands;
    prev.strong_winners_count += cur.strong_winner_hands;

    prev.unknown_winner_hands += cur.unknown_winner_hands;
    prev.weak_winner_hands += cur.weak_winner_hands;
    prev.middle_winner_hands += cur.middle_winner_hands;
    prev.strong_winner_hands += cur.strong_winner_hands;

    prev.unknown_winner_gross_profit += cur.unknown_winner_gross_profit;
    prev.weak_winner_gross_profit += cur.weak_winner_gross_profit;
    prev.middle_winner_gross_profit += cur.middle_winner_gross_profit;
    prev.strong_winner_gross_profit += cur.strong_winner_gross_profit;

    prev.unknown_winner_gross_profit_bb += cur.unknown_winner_gross_profit_bb;
    prev.weak_winner_gross_profit_bb += cur.weak_winner_gross_profit_bb;
    prev.middle_winner_gross_profit_bb += cur.middle_winner_gross_profit_bb;
    prev.strong_winner_gross_profit_bb += cur.strong_winner_gross_profit_bb;

    prev.unknown_winner_gross_profit_usd += cur.unknown_winner_gross_profit_usd;
    prev.weak_winner_gross_profit_usd += cur.weak_winner_gross_profit_usd;
    prev.middle_winner_gross_profit_usd += cur.middle_winner_gross_profit_usd;
    prev.strong_winner_gross_profit_usd += cur.strong_winner_gross_profit_usd;

    prev.unknown_winner_rake += cur.unknown_winner_rake;
    prev.weak_winner_rake += cur.weak_winner_rake;
    prev.middle_winner_rake += cur.middle_winner_rake;
    prev.strong_winner_rake += cur.strong_winner_rake;

    prev.unknown_winner_rake_bb += cur.unknown_winner_rake_bb;
    prev.weak_winner_rake_bb += cur.weak_winner_rake_bb;
    prev.middle_winner_rake_bb += cur.middle_winner_rake_bb;
    prev.strong_winner_rake_bb += cur.strong_winner_rake_bb;

    prev.unknown_winner_rake_usd += cur.unknown_winner_rake_usd;
    prev.weak_winner_rake_usd += cur.weak_winner_rake_usd;
    prev.middle_winner_rake_usd += cur.middle_winner_rake_usd;
    prev.strong_winner_rake_usd += cur.strong_winner_rake_usd;

    // Add loser statistics
    prev.unknown_losers_count += cur.unknown_losers_count;
    prev.weak_losers_count += cur.weak_losers_count;
    prev.middle_losers_count += cur.middle_losers_count;
    prev.strong_losers_count += cur.strong_losers_count;

    prev.unknown_loser_hands += cur.unknown_loser_hands;
    prev.weak_loser_hands += cur.weak_loser_hands;
    prev.middle_loser_hands += cur.middle_loser_hands;
    prev.strong_loser_hands += cur.strong_loser_hands;

    prev.unknown_loser_gross_profit += cur.unknown_loser_gross_profit;
    prev.weak_loser_gross_profit += cur.weak_loser_gross_profit;
    prev.middle_loser_gross_profit += cur.middle_loser_gross_profit;
    prev.strong_loser_gross_profit += cur.strong_loser_gross_profit;

    prev.unknown_loser_gross_profit_bb += cur.unknown_loser_gross_profit_bb;
    prev.weak_loser_gross_profit_bb += cur.weak_loser_gross_profit_bb;
    prev.middle_loser_gross_profit_bb += cur.middle_loser_gross_profit_bb;
    prev.strong_loser_gross_profit_bb += cur.strong_loser_gross_profit_bb;

    prev.unknown_loser_gross_profit_usd += cur.unknown_loser_gross_profit_usd;
    prev.weak_loser_gross_profit_usd += cur.weak_loser_gross_profit_usd;
    prev.middle_loser_gross_profit_usd += cur.middle_loser_gross_profit_usd;
    prev.strong_loser_gross_profit_usd += cur.strong_loser_gross_profit_usd;

    prev.unknown_loser_rake += cur.unknown_loser_rake;
    prev.weak_loser_rake += cur.weak_loser_rake;
    prev.middle_loser_rake += cur.middle_loser_rake;
    prev.strong_loser_rake += cur.strong_loser_rake;

    prev.unknown_loser_rake_bb += cur.unknown_loser_rake_bb;
    prev.weak_loser_rake_bb += cur.weak_loser_rake_bb;
    prev.middle_loser_rake_bb += cur.middle_loser_rake_bb;
    prev.strong_loser_rake_bb += cur.strong_loser_rake_bb;

    prev.unknown_loser_rake_usd += cur.unknown_loser_rake_usd;
    prev.weak_loser_rake_usd += cur.weak_loser_rake_usd;
    prev.middle_loser_rake_usd += cur.middle_loser_rake_usd;
    prev.strong_loser_rake_usd += cur.strong_loser_rake_usd;

    return prev;
  }, createDefaultStat());

  const summarizedResult = summarizeOpponent(result);

  return { ...summarizedResult, categorized: result };
};

export const countHeroTotal = (arr: PeriodStatisticsDto[]): HeroResultsDto => {
  return arr.reduce((prev, cur) => {
    prev.hero_count += cur.hero_results.hero_count;
    prev.hero_hands_count += cur.hero_results.hero_hands_count;
    prev.hero_gross_profit += cur.hero_results.hero_gross_profit;
    prev.hero_gross_profit_bb += cur.hero_results.hero_gross_profit_bb;
    prev.hero_gross_profit_usd += cur.hero_results.hero_gross_profit_usd;
    prev.hero_rake += cur.hero_results.hero_rake;
    prev.hero_rake_bb += cur.hero_results.hero_rake_bb;
    prev.hero_rake_usd += cur.hero_results.hero_rake_usd;

    return prev;
  }, createDefaultHero());
};

export const countOpponentTotal = (arr: PeriodStatisticsDto[]): CountTotalResult => {
  return countTotal(arr.map((i) => i.opponent_results));
};

export const countSubOpponentsTotal = (arr: PeriodStatisticsDto[]): CountTotalResult[] => {
  return arr
    .reduce((prev, cur) => {
      cur.sub_opponent_results.forEach((val, idx) => {
        const arr = prev[idx] || [];
        arr.push(val);
        prev[idx] = arr;
      });

      return prev;
    }, [] as PlayersCategorizedResultsDto[][])
    .map((arr) => countTotal(arr));
};

export const filterDataByFilters = (
  data: PlayerTimingsPeriodDto[] | PlayerTimingsDurationPointDto[],
  filters: typeof defaultTimingFilters
) => {
  return data.filter(
    (entry) =>
      filters.actionStages.includes(entry.actionStage) &&
      filters.actionTypes.includes(entry.actionType) &&
      filters.playerPositions.includes(entry.playerPosition)
  );
};
